import React, { useState, useMemo, useCallback, useEffect, useRef, useLayoutEffect } from 'react';
import LoadingSpinner from '../components/tailwind/LoadingSpinner';
import { useItemBundle } from '../hooks';
import { Breakpoints, SHOW_LOADER_DELAY } from '../settings';
import { Provider, useDispatch, useSelector, useStore } from 'react-redux';
import { useAppDispatch } from '@/store';
import Cookies from 'js-cookie';
import * as Actions from '../actions/pdp.actions';
import { PDPViews } from '@types';
import ScrollTo from '@/components/tailwind/ScrollTo';

import { Designer } from '@c2s-npm/plumreact';
import axios from 'axios';
import { Header as DesignerHeader } from '../features/pdp/designer';
import ItemInfo from '../features/pdp/designer/ItemInfo';
import useAsset from '@features/partner/hooks/useAsset';
import { formatCurrency } from '@utils/formatters.util';
import { useMediaQuery } from 'react-responsive';
import classNames from 'classnames';
import CustomShopModal, { CustomShopModalRef } from '@features/pdp/designer/CustomShopModal';

/*
Use a wrapper when bundle info is present to conditionally run useItemBundle without breaking the React hook rules
*/
const ProductDetailsDesigner = (props) => {
  const { details, inlineBundleData } = useSelector((state: any) => state.pdpItem);

  return inlineBundleData ? (
    <ProductWithBundle {...props} inlineBundleData={inlineBundleData} shopItemId={details.id} />
  ) : (
    <ProductDetailsCore {...props} />
  );
};

const ProductWithBundle = ({ shopItemId, inlineBundleData, ...props }) => {
  const [bundleInfo, onChoiceChange] = useItemBundle({
    bundleData: inlineBundleData,
    forcedSelectedIds: { 0: shopItemId },
  });

  return <ProductDetailsCore {...props} bundleInfo={bundleInfo} />;
};

const ProductDetailsCore = (props) => {
  const { loading } = useSelector((state: any) => state.pdpItem);

  return (
    <React.Fragment>
      {loading && <LoadingSpinner delay={SHOW_LOADER_DELAY} />}
      <section className='product-main'>
        <CustomDesigner />
      </section>
    </React.Fragment>
  );
};

const DesignerTitle = ({ partnerConfig, designerItem, config, graphicType, selectedFace }) => {
  const miyoFeeItems = (config.find((c) => c.face === selectedFace)?.miyoFees || []).filter(
    (f) => (!f.graphicType || f.graphicType === graphicType) && f.feeKey === 'face'
  );

  const lowestRateItem = [...(miyoFeeItems || {})].reduce(
    (acc, curr) => (acc.rate < curr.rate ? acc : curr),
    miyoFeeItems?.[0] || {}
  );

  const hasMultipleSides = config?.length > 1;

  // added order classes as I will insert the FaceToggle component in the middle between the partner logo and the fees
  return (
    <>
      {!!partnerConfig?.partner && (
        <div className='tw-order-1 tw-mb-5'>
          {partnerConfig?.partner?.logoUrl && (
            <img
              src={partnerConfig.partner.logoUrl}
              className='tw-h-full tw-max-h-[48px] tw-w-auto tw-max-w-full'
            />
          )}
        </div>
      )}
      {!partnerConfig?.partner && (
        <p className='tw-order-3 tw-mb-0 tw-text-center tw-text-2xs tw-font-normal tw-text-brand-black sm:tw-text-xs md:tw-text-sm lg:tw-mb-6'>
          {lowestRateItem?.rate > 0 ? <>
            Personalization starts at{' '}
            <b
                className={classNames({
                  'tw-text-onsale': !!lowestRateItem?.usualRate,
                })}>
              {formatCurrency(lowestRateItem?.rate || 0)}
            </b>
          </> : <b
              className={classNames({
                'tw-text-onsale': !!lowestRateItem?.usualRate,
              })}>
            Free Personalization
          </b>}
          {' '}
          {lowestRateItem.usualRate && (
              <span className='tw-line-through'>{formatCurrency(lowestRateItem?.usualRate || 0)}</span>
          )}
          {' '}
          {hasMultipleSides && 'per side'}
        </p>
      )}
    </>
  );
};

export const CustomDesigner = ({ defaultFace = 'empty' }) => {
  const dispatch = useAppDispatch();
  const wrapperRef = useRef(null);
  const designerRef = useRef(null);
  const pdpItem = useSelector((state: any) => state.pdpItem);
  const { details, miyo = {}, design } = pdpItem;
  const setDesign = (design) => dispatch(Actions.setDesign(design));

  const { itemId } = details;

  const {
    designId,
    lineId: lineIdQueryParam,
    promoCodeId: promoCodeIdQueryParam,
    partnerId: partnerIdQueryParam,
    edit: editQueryParam,
    copy: copyQueryParam,
    debug: debugQueryParam,
    export: exportQueryParam,
    secret: secretQueryParam,
    group: groupQueryParam,
    qty: quantityQueryParam,
  } = Object.fromEntries(new URLSearchParams(window.location.search).entries());
  const debug = (debugQueryParam ?? Cookies.get('miyoDebug')) === 'true';

  const withSecret = (prefix) => (secretQueryParam ? `${prefix}&secret=${secretQueryParam}` : '');

  const [config, setConfig] = useState(null);
  const [uploads, setUploads] = useState(null);
  const [library, setLibrary] = useState(null);
  const [loading, setLoading] = useState(null);
  const [exportStatus, setExportStatus] = useState(null);
  const [customShopAsked, setCustomShopAsked] = useState(false);
  // don't remove, responsible for loading default design with asset
  useAsset();

  const { partnerConfig = {} } = miyo || {};
  const partnerId = partnerConfig?.partner?.id;
  const limitAssets = !!partnerConfig?.partner?.limitAssets;
  const assetId = miyo?.asset?.id;

  const miyoFee = miyo?.faceFee?.rate || 0;

  const isSmallCanvas = Math.min(...(config || []).map((c) => c.print.height)) <= 12;

  const canArtwork = !isSmallCanvas;
  const canMonogram = !isSmallCanvas;
  const canJersey = !isSmallCanvas;
  const canUpload = !isSmallCanvas && !limitAssets;
  const canGroup = !promoCodeIdQueryParam;

  // TODO drive based on cart?
  const quantityRef = useRef<HTMLInputElement>(null);
  const [quantity, setQuantity] = useState(1);

  useEffect(() => {
    (async () => {
      if (itemId) {
        setLoading('Loading product configuration...');
        const { data } = await axios.get(`/custom/config/${itemId}`);
        setConfig(data);
        setLoading(false);
      } else {
        setConfig(false);
      }
    })();
  }, [itemId]);
  const customShopModalRef = useRef<CustomShopModalRef>(null);

  useEffect(() => {
    (async () => {
      setLoading('Loading asset library...');
      const { data } = await axios.get(
        '/custom/assets?' +
          (debug ? '&debug=true' : '') +
          (partnerIdQueryParam ? '&partnerId=' + partnerIdQueryParam : '')
      );
      setLibrary(data);
      setLoading(false);
    })();
  }, []);

  useEffect(() => {
    (async () => {
      if (!designId) {
        setLoading('Loading user design library...');
        const { data } = await axios.get('/custom/designs');
        setUploads(data);
        setLoading(false);
      }
    })();
  }, []);

  useLayoutEffect(() => {
    const parsed = parseInt(quantityQueryParam);
    setQuantity(isNaN(parsed) ? 1 : parsed);
  }, []);

  useEffect(() => {
    (async () => {
      if (designId) {
        try {
          setLoading('Loading design...');
          const { data } = await axios.get(`/custom/design/${designId}` + withSecret('?'));
          console.log({ data });
          const withUrls = Object.assign(data, {
            faces: data.faces.map((f) =>
              Object.assign(f, {
                elements: f.elements.map((e) =>
                  e.hash
                    ? Object.assign(e, {
                        url: `/custom/designs/${!!secretQueryParam ? e.fileId : e.hash}` + withSecret('?'),
                      })
                    : e
                ),
              })
            ),
          });

          // fake uploads
          const uploads = withUrls.faces.reduce((a, f) => {
            f.elements.reduce((a, e) => {
              if (e.elementType === 'file') {
                a.push({
                  id: e.fileId,
                  url: e.url,
                  filename: e.filename,
                  mimetype: e.mimetype,
                });
              }
              return a;
            }, a);
            return a;
          }, []);

          if (copyQueryParam) {
            delete withUrls.id;
          } else if (editQueryParam) {
            withUrls.copyId = withUrls.id;
            delete withUrls.id;
          }

          setUploads(uploads);
          setDesign(withUrls);
          setLoading(false);
        } catch (e) {
          console.error(e);
          alert('Failed to load design');
          setLoading(false);
        }
      }
    })();
  }, [designId]);

  // skip to the first empty face if configured to
  useEffect(() => {
    if (!!design && !!designerRef.current && defaultFace === 'empty') {
      const available = config.map((c) => c.face);
      const current = design.faces.map((f) => f.face);
      const emptyFace = available.filter((f) => !current.includes(f))[0];
      if (emptyFace) {
        designerRef.current.selectFace(emptyFace);
      }
    }
  }, [defaultFace, design, config, designerRef.current]);

  const processing = !library || !config;

  const handleUpload = (e) => {
    e && setUploads([e].concat(uploads || []));
  };

  const checkCustomShop = () => {
    if (customShopAsked) return false;
    if (quantityQueryParam === quantity.toString()) return false;

    const minimumOrderCount = getMinimumOrderQuantityForCustomShopPopup(details);

    if (!!minimumOrderCount && quantity > minimumOrderCount) {
      customShopModalRef.current?.openModal();
      return true;
    }
    return false;
  };

  useEffect(() => {
    checkCustomShop();
  }, [quantity]);

  const handleSubmit = async (e) => {
    setLoading(true);

    try {
      const hash = (s) =>
        `${s.split('').reduce((a, b) => {
          a = (a << 5) - a + b.charCodeAt(0);
          return a & a;
        }, 0)}`;

      const toFile = async (base64, filename) => {
        const split = base64.split(',');
        //const data = split[0].indexOf('base64') >= 0 ? atob(split[1]) : decodeURI(split[1]);
        const mime = split[0].split(':')[1].split(';')[0];

        //const encoder = new TextEncoder();
        // const bytes = encoder.encode(data);
        const buffer = await (await fetch(base64)).arrayBuffer();
        const bytes = [buffer]; // new Uint8Array(buffer);

        return new File(bytes, filename, { type: mime });
      };

      const { faces, files } = e.faces.reduce(
        ({ faces, files }, face) => {
          const hashedProductPreview = face.face + '.' + hash(face.productPreview);
          const hashedPreview = face.face + '.' + hash(face.preview);
          // const hashedRender = face.face + '.' + hash(face.render);

          const { elements, files: elementFiles } = face.elements.reduce(
            ({ elements, files }, element) => {
              if (element.elementType === 'uploaded' || element.elementType === 'file') {
                const hashedUrl = hash(element.url);
                return {
                  elements: elements.concat([Object.assign({}, element, { url: hashedUrl })]),
                  files: files.concat([{ hash: hashedUrl, url: element.url }]),
                };
              } else {
                return {
                  elements: elements.concat([element]),
                  files,
                };
              }
            },
            { elements: [], files }
          );

          const hashedFace = Object.assign({}, face, {
            elements,
            productPreview: hashedProductPreview,
            preview: hashedPreview,
            // render: hashedRender,
          });
          delete hashedFace.render;
          return {
            faces: faces.concat([hashedFace]),
            files: files.concat(elementFiles).concat([
              { hash: hashedProductPreview, url: face.productPreview, base64: true },
              { hash: hashedPreview, url: face.preview, base64: true },
              // engraved face.render is a DXF file as string, color is a PNG data url
              // { hash: hashedRender, url: face.render, base64: face.render?.startsWith('data:') },
            ]),
          };
        },
        { faces: [], files: [] }
      );

      if (e.preview) {
        files.push({
          hash: 'preview.product.all',
          url: e.preview,
          base64: true,
        });
        delete e.preview;
      }

      const formData = new FormData();
      await Promise.all(
        files.map(async ({ hash, url, base64 }) => {
          const file = base64 ? await toFile(url, hash) : new File([url], hash, { type: 'image/x-dxf' });
          formData.append('file', file);
        })
      );

      formData.append(
        'design',
        JSON.stringify(
          Object.assign(e, {
            shopItemId: details.id,
            faces,
            id: design?.id,
            partnerId,
            quantity: quantity || 1,
            userAgent: window?.navigator?.userAgent,
          })
        )
      );
      if (design?.copyId) {
        formData.append('copyId', design.copyId);
      }
      if (lineIdQueryParam) {
        formData.append('lineId', lineIdQueryParam);
      }
      if (promoCodeIdQueryParam) {
        formData.append('promoCodeId', promoCodeIdQueryParam);
      }

      if (!design?.id || design?.itemId !== details.id) {
        const response = await axios.post('/cart/custom', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        window.location = jsRoutes.controllers.Cart.view().url;
      } else {
        // update, don't redirect
        const response = await axios.post('/custom/design/update' + withSecret('?'), formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        if (editQueryParam || copyQueryParam) {
          window.location = jsRoutes.controllers.Cart.view().url;
        } else {
          alert('Design updated');
          setExportStatus('complete');
          setLoading(false);
        }
      }
    } catch (e) {
      console.error(e);
      alert('There was a problem saving your design.');
      setExportStatus('error');
      setLoading(false);
    }
  };

  const handleInit = () => {
    if (exportQueryParam) {
      designerRef.current.submit();
    }
  };

  const handleBack = () => {
    dispatch(Actions.setView(PDPViews.DEFAULT));
  };

  const itemInfoRef = useRef(null);
  const pdpStore = useStore();

  const ItemInfoComponent = useCallback(
    (p) => (
      <Provider store={pdpStore}>
        <ItemInfo
          ref={itemInfoRef}
          {...{ quantity, setQuantity }}
          {...p}
          promoCodeId={promoCodeIdQueryParam}
          lineId={lineIdQueryParam}
        />
      </Provider>
    ),
    [quantity, setQuantity, itemInfoRef, customShopAsked, promoCodeIdQueryParam, lineIdQueryParam]
  );

  const DesignerHeaderComponent = useCallback(
    (p) => (
      <Provider store={pdpStore}>
        <DesignerHeader {...p} />
      </Provider>
    ),
    []
  );

  const designerItem = {
    displayNameOnPage: details?.displayNameOnPage,
    url: details?.url,
    miyoFee,
    typeId: details?.attributes?.typeId,
  };
  const TitleComponent = useCallback(
    (p) => <DesignerTitle {...p} {...{ designerItem, partnerConfig, config }} />,
    [designerItem, partnerConfig]
  );

  const isMobile = useMediaQuery({ maxWidth: Breakpoints.LG });
  const fullscreen = useMemo(() => isMobile && config?.length > 0, [isMobile, config]);
  const graphicTypes = useMemo(() => {
    return [
      ...new Set(
        (config || []).flatMap((c) => {
          return Object.keys(c.miyoConfig?.graphicType || {}).filter(
            (key) => window.DD_ENV === 'dev' || c.miyoConfig.graphicType[key] === 'prod'
          );
        })
      ),
    ];
  }, [config]);

  useLayoutEffect(() => {
    const timeoutId = setTimeout(() => {
      const hasNoScroll = document.querySelector('#main').classList.contains('miyo-no-scroll');
      if (fullscreen) {
        if (!hasNoScroll) {
          document.body.scrollTop = 0;
          document.querySelector('#main').classList.add('miyo-no-scroll');
          document.documentElement.classList.add('miyo-no-scroll');
        }
      } else {
        if (hasNoScroll) {
          document.body.scrollTop = 0;
          document.querySelector('#main').classList.remove('miyo-no-scroll');
          document.documentElement.classList.remove('miyo-no-scroll');
        }
      }
    }, 300);

    return () => {
      console.log('unmounting designer');
      if (timeoutId) clearTimeout(timeoutId);
      const hasNoScroll = document.querySelector('#main').classList.contains('miyo-no-scroll');
      if (hasNoScroll) {
        document.body.scrollTop = 0;
        document.querySelector('#main').classList.remove('miyo-no-scroll');
        document.documentElement.classList.remove('miyo-no-scroll');
      }
    };
  }, [fullscreen]);

  return (
    <div id='miyo' className='tw-relative' ref={wrapperRef}>
      <ScrollTo />
      {loading && <LoadingSpinner title={loading} />}
      {exportStatus && <span id='designerExportDone' className={exportStatus} style={{ display: 'none' }} />}
      <CustomShopModal
        ref={customShopModalRef}
        quantity={quantity}
        onAccept={() => {
          window.location.href = details.customizationUrl;
        }}
        onCancel={() => {
          setCustomShopAsked(true);
        }}
      />

      {graphicTypes.length > 0 && (
        <Designer
          ref={designerRef}
          contentClassName='tw-mx-auto tw-max-w-[1456px] tw-px-4'
          {...{
            config,
            design,
            graphicTypes,
            library,
            processing,
            itemInfoComponent: ItemInfoComponent,
            getTotalPrice: itemInfoRef.current?.getTotalPrice,
            designerHeaderComponent: DesignerHeaderComponent,
            titleComponent: TitleComponent,
            onSubmit: handleSubmit,
            onUpload: handleUpload,
            onInit: handleInit,
            onBack: handleBack,
            previewUrl: '/custom/preview',
            uploadUrl: '/custom/designs',
            uploaded: uploads,
            withUpload: canUpload,
            withMonogram: canMonogram,
            withJersey: canJersey,
            withArtwork: canArtwork,
            withGroup: canGroup,
            debug,
            item: designerItem,
          }}
        />
      )}
      {!loading && graphicTypes.length === 0 && (
        <div className='display-wrapper tw-mt-5 tw-text-center tw-text-lg'>
          <em>Product not customizable</em>
        </div>
      )}
    </div>
  );
};

const HARD_COOLER_TYPE_IDS = [379, 70, 121, 166, 167, 168, 169, 355, 441, 442, 388, 410, 523, 536];
const SOFT_COOLER_TYPE_IDS = [4];
const ULTRA_TOUGH_TYPE_IDS = [390];

const moqOverrides = {
  5: HARD_COOLER_TYPE_IDS,
  10: [...SOFT_COOLER_TYPE_IDS, ...ULTRA_TOUGH_TYPE_IDS],
};

const MAX_PERCENTAGE_OF_CASE_COUNT_FOR_CUSTOM_SHOP_QUOTE = 0.9; // 90%

const getMinimumOrderQuantityForCustomShopPopup = (item) => {
  const _getMinimumOrderQuantity = (item) => {
    for (const [moq, typeIds] of Object.entries(moqOverrides)) {
      if (typeIds.includes(item.attributes.typeId)) {
        return parseInt(moq);
      }
    }
    return item.caseCount;
  };

  const moq = _getMinimumOrderQuantity(item);
  return moq ? moq * MAX_PERCENTAGE_OF_CASE_COUNT_FOR_CUSTOM_SHOP_QUOTE : undefined;
};

export default ProductDetailsDesigner;
